import React, { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Lock, ArrowUpCircle, CheckCircle, Sparkles } from 'lucide-react'
import { UpgradePrompt } from './UpgradePrompt'

interface FeatureRestrictedProps {
  featureName: string
  description: string
  currentPlan: 'starter' | 'growth' | 'leadership'
  requiredPlan: 'growth' | 'leadership'
  icon?: React.ReactNode
}

export function FeatureRestricted({
  featureName,
  description,
  currentPlan,
  requiredPlan,
  icon,
}: FeatureRestrictedProps) {
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false)

  const getPlanBadge = (plan: string) => {
    if (plan === 'leadership') {
      return <Badge className="bg-sky-500">Leadership</Badge>
    }
    if (plan === 'growth') {
      return <Badge className="bg-blue-500">Growth</Badge>
    }
    return <Badge variant="outline">Starter</Badge>
  }

  const getFeaturesList = () => {
    if (requiredPlan === 'growth') {
      return [
        'Complete Finance Management',
        'Attendance Tracking & Analytics',
        'Advanced Ministry Tools (Devotions, Fasting, Counseling, Evangelism)',
        'Media Library',
        'Up to 2,000 members',
        '50GB storage',
        '1,000 SMS per month',
      ]
    }
    if (requiredPlan === 'leadership') {
      return [
        'Everything in Growth',
        'HR & Payroll System',
        'White-label Mobile App',
        'API Access',
        'Unlimited members & storage',
        'Unlimited locations',
        '3,000+ SMS per month',
      ]
    }
    return []
  }

  return (
    <>
      <div className="flex items-center justify-center min-h-[600px] p-6">
        <Card className="max-w-2xl w-full">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="relative">
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-sky-500">
                  {icon || <Lock className="h-10 w-10 text-white" />}
                </div>
                <div className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-yellow-500 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
              </div>
            </div>
            <CardTitle className="text-2xl mb-2">{featureName}</CardTitle>
            <CardDescription className="text-base">{description}</CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Current vs Required Plan */}
            <div className="flex items-center justify-center gap-4">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Your Plan</p>
                {getPlanBadge(currentPlan)}
              </div>
              <ArrowUpCircle className="h-6 w-6 text-muted-foreground" />
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Required</p>
                {getPlanBadge(requiredPlan)}
              </div>
            </div>

            {/* Features Included */}
            <div className="bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-950 dark:to-sky-950 rounded-lg p-6 border">
              <h4 className="font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                What you'll get with {requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1)}
              </h4>
              <ul className="space-y-3">
                {getFeaturesList().map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Pricing */}
            <div className="text-center p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Upgrade Price</p>
              <p className="text-3xl font-bold">
                GHS {requiredPlan === 'growth' ? '1,300' : '3,900'}
                <span className="text-base font-normal text-muted-foreground">/month</span>
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                or GHS {requiredPlan === 'growth' ? '12,480' : '37,440'}/year
              </p>
            </div>

            {/* CTA Button */}
            <Button
              size="lg"
              className="w-full bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-600 hover:to-sky-600"
              onClick={() => setShowUpgradeDialog(true)}
            >
              <ArrowUpCircle className="h-5 w-5 mr-2" />
              Upgrade to {requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1)}
            </Button>

            <p className="text-center text-xs text-muted-foreground">
              Questions? Contact our support team for assistance
            </p>
          </CardContent>
        </Card>
      </div>

      <UpgradePrompt
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        feature={featureName}
        currentPlan={currentPlan}
      />
    </>
  )
}
